import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Email, UserEmailFilter, EmailTypeFilter } from '../types';
import EmailListView from '../components/email/EmailListView';
import EmailDetailView from '../components/email/EmailDetailView';
import EmailComposer, { EmailSendData, EmailSendResult } from '../components/email/EmailComposer';
import EmailConversation from '../components/EmailConversation';
import { useSSE } from '../hooks/useWebSocket';
import { apiService } from '../services/api';
import { getApiBaseUrl } from '../config/env';
import { envConfig } from '../config/env';
import EnvIndicator from '../components/EnvIndicator';

interface EmailListPageProps {
  showComposer?: boolean;
  onComposeClose?: () => void;
}

const EmailListPage: React.FC<EmailListPageProps> = ({ showComposer = false, onComposeClose }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [userEmailFilter, setUserEmailFilter] = useState<UserEmailFilter>('all');
  const [emailTypeFilter, setEmailTypeFilter] = useState<EmailTypeFilter>('all');
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenToggleLoading, setHiddenToggleLoading] = useState(false);
  const [leftWidth, setLeftWidth] = useState(440); // 固定宽度440像素
  const [isDragging, setIsDragging] = useState(false);
  
  // 邮件撰写相关状态
  const [composerOpen, setComposerOpen] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<Email | null>(null);

  // 分页状态
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    limit: 10,
    hasMore: false
  });

  // SSE连接
  const { connectionStatus } = useSSE({
    url: `${envConfig.API_BASE_URL}/sse/emails`,
    onMessage: (message) => {
      console.log('收到SSE消息:', message);
      
      if (message.type === 'new_email') {
        // 收到新邮件通知，根据当前设置决定是否显示
        console.log('收到新邮件通知:', message.data);
        
        // 检查是否应该显示这封邮件
        const isUserEmail = message.data.is_user_email || false;
        const shouldShow = showHidden || isUserEmail; // 如果显示隐藏邮件或者是用户邮件，则显示
        
        if (!shouldShow) {
          console.log('系统邮件且当前隐藏系统邮件，跳过显示');
          // 仍然显示通知日志，但不插入到列表中
          console.log(`✉️ 新系统邮件（已隐藏）: ${message.data.subject}`);
          return;
        }
        
        // 检查是否已存在该邮件（避免重复）
        setEmails(prevEmails => {
          const exists = prevEmails.some(email => email.id === message.data.id);
          if (exists) {
            console.log('邮件已存在，跳过插入');
            return prevEmails;
          }
          
          // 创建新邮件对象（使用SSE中的完整数据）
          const newEmail: Email = {
            id: message.data.id,
            message_id: message.data.message_id,
            subject: message.data.subject,
            sender_email: message.data.sender_email,
            sender_name: message.data.sender_name || '',
            recipient_email: message.data.recipient_email || '',
            content: message.data.content || '',
            html_content: message.data.html_content || '',
            translated_content: message.data.translated_content,
            summary: message.data.summary,
            status: message.data.status,
            category: message.data.category || 'other',
            received_at: message.data.received_at || new Date().toISOString(),
            sent_at: message.data.sent_at,
            replied_at: message.data.replied_at,
            is_user_email: isUserEmail,
            priority: message.data.priority || 1,
            confidence_score: message.data.confidence_score || 0,
            auto_reply_content: message.data.auto_reply_content,
            tags: message.data.tags,
            email_type: message.data.email_type || 'received',
            ai_analysis_status: message.data.ai_analysis_status || 'none',
            has_attachments: message.data.has_attachments || false,
            attachment_count: message.data.attachment_count || 0,
            attachments_info: message.data.attachments_info || [],
            sender_rule: message.data.sender_rule
          };
          
          // 按时间排序插入邮件（最新的在最下面）
          const newEmailTime = new Date(newEmail.received_at).getTime();
          const updatedEmails = [...prevEmails];
          
          // 找到正确的插入位置（按时间升序排列，最新的在最下面）
          let insertIndex = updatedEmails.length;
          for (let i = updatedEmails.length - 1; i >= 0; i--) {
            const currentEmailTime = new Date(updatedEmails[i].received_at).getTime();
            if (newEmailTime > currentEmailTime) {
              insertIndex = i + 1;
              break;
            }
          }
          
          // 在正确位置插入新邮件
          updatedEmails.splice(insertIndex, 0, newEmail);
          
          console.log(`插入新邮件到位置 ${insertIndex}，按时间排序`);
          return updatedEmails;
        });
        
        // 显示通知
        if (isUserEmail) {
          // 只为用户邮件显示通知
          if (Notification.permission === 'granted') {
            new Notification('新邮件', {
              body: `来自 ${message.data.sender_email}: ${message.data.subject}`,
              icon: '/favicon.ico'
            });
          }
        }
        
        // 显示页面内通知
        console.log(`✉️ 新${isUserEmail ? '用户' : '系统'}邮件: ${message.data.subject}`);
        
      } else if (message.type === 'new_sent_email') {
        // 邮件发送成功，按时间排序添加到邮件列表
        console.log('收到邮件发送成功通知:', message.data);
        
        const emailData = message.data;
        
        // 检查是否已存在该邮件（避免重复）
        setEmails(prevEmails => {
          const exists = prevEmails.some(email => email.id === emailData.id);
          if (exists) {
            console.log('已发送邮件已存在，跳过插入');
            return prevEmails;
          }
          
          // 创建新邮件对象（使用SSE中的完整数据）
          const newEmail: Email = {
            id: emailData.id || Date.now(),
            message_id: emailData.message_id,
            subject: emailData.subject,
            sender_email: emailData.sender_email,
            sender_name: emailData.sender_name || '',
            recipient_email: emailData.recipient_email || '',
            content: emailData.content || '',
            html_content: emailData.html_content || '',
            translated_content: emailData.translated_content,
            summary: emailData.summary,
            received_at: emailData.received_at || emailData.sent_at || new Date().toISOString(),
            sent_at: emailData.sent_at || new Date().toISOString(),
            replied_at: emailData.replied_at,
            status: 'read', // 发送的邮件默认为已读
            category: emailData.category || 'other',
            priority: emailData.priority || 0,
            confidence_score: emailData.confidence_score || 0,
            auto_reply_content: emailData.auto_reply_content,
            tags: emailData.tags,
            is_user_email: emailData.is_user_email || false,
            email_type: 'sent' as const,
            ai_analysis_status: emailData.ai_analysis_status || 'none',
            has_attachments: emailData.has_attachments || false,
            attachment_count: emailData.attachment_count || 0,
            attachments_info: emailData.attachments_info || [],
            sender_rule: emailData.sender_rule
          };
          
          // 按时间排序插入邮件（最新的在最下面）
          const newEmailTime = new Date(newEmail.sent_at || newEmail.received_at).getTime();
          const updatedEmails = [...prevEmails];
          
          // 找到正确的插入位置（按时间升序排列，最新的在最下面）
          let insertIndex = updatedEmails.length;
          for (let i = updatedEmails.length - 1; i >= 0; i--) {
            const currentEmailTime = new Date(updatedEmails[i].received_at).getTime();
            if (newEmailTime > currentEmailTime) {
              insertIndex = i + 1;
              break;
            }
          }
          
          // 在正确位置插入新邮件
          updatedEmails.splice(insertIndex, 0, newEmail);
          
          console.log(`插入新发送邮件到位置 ${insertIndex}，按时间排序`);
          return updatedEmails;
        });
        
        // 显示页面内通知
        console.log(`📤 新发送邮件: ${emailData.subject}`);
        
      } else if (message.type === 'connected') {
        console.log('SSE连接已建立');
      } else if (message.type === 'heartbeat') {
        // 心跳消息，保持静默
      } else if (message.type === 'ai_analysis_completed') {
        // AI分析完成，更新邮件状态
        const emailData = message.data;
        if (emailData?.id) {
          setEmails(prevEmails => 
            prevEmails.map(email => 
              email.id === emailData.id 
                ? { ...email, ai_analysis_status: 'completed' }
                : email
            )
          );
          console.log('✅ AI分析完成:', emailData.id);
        }
      } else if (message.type === 'ai_analysis_failed') {
        // AI分析失败，更新邮件状态
        const emailData = message.data;
        if (emailData?.id) {
          setEmails(prevEmails => 
            prevEmails.map(email => 
              email.id === emailData.id 
                ? { ...email, ai_analysis_status: 'failed' }
                : email
            )
          );
          console.log('❌ AI分析失败:', emailData.id);
        }
      } else if (message.type === 'email_sent') {
        // 邮件发送成功提示
        const emailData = message.data;
        console.log('✅ 邮件发送成功:', emailData);
        // 可以添加Toast提示或其他UI反馈
      } else if (message.type === 'email_attachments_processed') {
        // 邮件附件处理完成，更新邮件附件信息
        const attachmentData = message.data;
        console.log('📎 收到附件处理完成事件:', attachmentData);
        
        if (attachmentData?.id) {
          setEmails(prevEmails => 
            prevEmails.map(email => {
              // 通过邮件ID匹配邮件
              if (email.id === attachmentData.id) {
                return {
                  ...email,
                  has_attachments: attachmentData.has_attachments,
                  attachment_count: attachmentData.attachment_count,
                  attachments_info: attachmentData.attachments_info || []
                };
              }
              return email;
            })
          );
          console.log('✅ 邮件附件信息已更新:', attachmentData.message_id, '邮件ID:', attachmentData.id, '附件数量:', attachmentData.attachment_count);
        }
      }
    },
    onOpen: () => {
      console.log('SSE连接已建立');
    },
    onClose: () => {
      console.log('SSE连接已断开');
    },
    onError: (error) => {
      console.error('SSE连接错误:', error);
    }
  });

  // 请求通知权限
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // 获取邮件列表
  const fetchEmails = async (page: number = 1) => {
    try {
      setLoading(true);
      const offset = (page - 1) * pagination.limit;
      
      const response = await fetch(
        `${envConfig.API_BASE_URL}/emails?` + new URLSearchParams({
          limit: pagination.limit.toString(),
          offset: offset.toString(),
          show_hidden: showHidden.toString(),
          ...(userEmailFilter !== 'all' && { is_user_email: (userEmailFilter === 'user').toString() }),
          ...(emailTypeFilter !== 'all' && { email_type: emailTypeFilter })
        })
      );

      if (!response.ok) {
        throw new Error('获取邮件列表失败');
      }

      const data = await response.json();
      
      // 检查返回的数据结构
      console.log('API Response:', data);
      
      setEmails(data.emails || []);
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        total: data.pagination?.total || 0,
        hasMore: data.pagination?.has_next || false
      }));
    } catch (error) {
      console.error('获取邮件列表失败:', error);
      // 发生错误时重置状态
      setEmails([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
        hasMore: false
      }));
    } finally {
      setLoading(false);
    }
  };

  // 处理页码变更
  const handlePageChange = (page: number) => {
    fetchEmails(page);
  };

  // 初始加载
  useEffect(() => {
    fetchEmails(1);
  }, [showHidden, userEmailFilter, emailTypeFilter]);

  const handleShowHiddenToggle = async (checked: boolean) => {
    setHiddenToggleLoading(true);
    setShowHidden(checked);
    // fetchEmails 会在 useEffect 中自动调用
  };

  const handleEmailTypeFilterChange = (filter: EmailTypeFilter) => {
    setEmailTypeFilter(filter);
    // fetchEmails 会在 useEffect 中自动调用
  };

  const handleEmailSelect = (emailId: number) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleEmailClick = async (email: Email) => {
    // 如果邮件内容为空，先获取完整的邮件详情
    if (!email.content && !email.html_content) {
      try {
        console.log('邮件内容为空，获取完整详情:', email.id);
        const response = await fetch(`${getApiBaseUrl()}/emails/${email.id}`);
        if (response.ok) {
          const fullEmail = await response.json();
          
          // 更新邮件列表中的邮件数据
          setEmails(prevEmails => 
            prevEmails.map(e => 
              e.id === email.id ? { ...e, ...fullEmail } : e
            )
          );
          
          // 设置选中的邮件为完整数据
          setSelectedEmail({ ...email, ...fullEmail });
        } else {
          console.error('获取邮件详情失败:', response.status);
          setSelectedEmail(email);
        }
      } catch (error) {
        console.error('获取邮件详情失败:', error);
        setSelectedEmail(email);
      }
    } else {
      setSelectedEmail(email);
    }
  };

  // 处理回复邮件
  const handleReply = (email: Email) => {
    setReplyToEmail(email);
    setComposerOpen(true);
  };

  // 处理发送邮件
  const handleSendEmail = async (emailData: EmailSendData): Promise<EmailSendResult> => {
    try {
      // 构造API请求数据，修正字段名映射
      const apiData = {
        to: emailData.to,  // 修正：使用to而不是from_email
        subject: emailData.subject,
        content: emailData.content,
        html_content: emailData.html_content,
        reply_to_id: emailData.reply_to_id,
        account_id: emailData.account_id
      };

      // 发送邮件请求（异步）
      const response = await fetch(`${getApiBaseUrl()}/emails/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 429) {
          throw new Error('检测到重复发送，请稍后再试');
        }
        throw new Error(errorData.detail || '发送邮件失败');
      }

      const result = await response.json();
      const taskId = result.task_id;
      
      // 返回任务ID以便跟踪状态
      return { taskId };
      
    } catch (error) {
      console.error('发送邮件失败:', error);
      throw error;
    }
  };

  // 关闭撰写器
  const handleCloseComposer = () => {
    setComposerOpen(false);
    setReplyToEmail(null);
    if (onComposeClose) {
      onComposeClose();
    }
  };

  // 监听外部 showComposer 属性变化
  useEffect(() => {
    if (showComposer) {
      setComposerOpen(true);
      setReplyToEmail(null);
    }
  }, [showComposer]);

  // 拖拽处理
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const container = document.querySelector('.email-container');
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const newLeftWidth = e.clientX - rect.left;
    
    // 限制最小宽度300px，最大宽度为容器宽度的80%
    const minWidth = 300;
    const maxWidth = rect.width * 0.8;
    
    if (newLeftWidth >= minWidth && newLeftWidth <= maxWidth) {
      setLeftWidth(newLeftWidth);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging]);

  useEffect(() => {
    fetchEmails().finally(() => {
      setHiddenToggleLoading(false);
    });
  }, [userEmailFilter, showHidden, emailTypeFilter]);

  // 处理邮件状态更新
  const handleEmailUpdate = (emailId: number, updates: Partial<Email>) => {
    setEmails(prevEmails => 
      prevEmails.map(email => 
        email.id === emailId 
          ? { ...email, ...updates }
          : email
      )
    );
    
    // 如果选中的邮件被更新，也更新选中邮件的状态
    if (selectedEmail && selectedEmail.id === emailId) {
      setSelectedEmail(prevEmail => 
        prevEmail ? { ...prevEmail, ...updates } : null
      );
    }
  };

  // 处理AI分析
  const handleAIAnalysis = async (email: Email) => {
    try {
      // 调用AI分析API - 使用新的触发助手API
      const response = await fetch(`${getApiBaseUrl()}/ai/trigger-assistant/1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email_id: email.id })
      });

      if (!response.ok) {
        throw new Error('AI分析请求失败');
      }

      const result = await response.json();
      console.log('AI分析结果:', result);
      
      // 显示成功提示
      alert('AI分析已完成，请查看邮件详情页面的分析结果');
      
      // 可以在这里添加更新UI的逻辑
      // 例如刷新邮件数据以获取最新的分析结果
      
    } catch (error) {
      console.error('AI分析失败:', error);
      alert('AI分析失败，请稍后重试');
    }
  };

  return (
    <>
      <div className="flex h-full email-container">
        {/* 邮件列表 */}
        <div 
          className="border-r border-gray-200 transition-all duration-200 flex-shrink-0"
          style={{ width: `${leftWidth}px` }}
        >
          <EmailListView
            emails={emails}
            loading={loading}
            selectedEmails={selectedEmails}
            onEmailSelect={handleEmailSelect}
            onEmailClick={handleEmailClick}
            onRefresh={() => fetchEmails(pagination.currentPage)}
            onFilterChange={setUserEmailFilter}
            onEmailTypeFilterChange={handleEmailTypeFilterChange}
            onShowHiddenToggle={handleShowHiddenToggle}
            userEmailFilter={userEmailFilter}
            emailTypeFilter={emailTypeFilter}
            showHidden={showHidden}
            hiddenToggleLoading={hiddenToggleLoading}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>

        {/* 拖拽分隔条 */}
        <div
          className={`w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0 ${
            isDragging ? 'bg-blue-500' : ''
          }`}
          onMouseDown={handleMouseDown}
        />

        {/* 邮件详情 */}
        <div 
          className="transition-all duration-200 h-full overflow-hidden flex flex-col flex-1"
        >
          <EmailDetailView
            email={selectedEmail}
            onBack={() => setSelectedEmail(null)}
            onReply={handleReply}
            onEmailUpdate={handleEmailUpdate}
            onAIAnalysis={handleAIAnalysis}
          />
        </div>
      </div>

      {/* 邮件撰写器 */}
      <EmailComposer
        isOpen={composerOpen}
        onClose={handleCloseComposer}
        replyToEmail={replyToEmail}
        onSend={handleSendEmail}
      />
    </>
  );
};

export default EmailListPage; 